import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { BuildingSkeleton } from "shared/skeletons/building.skeleton";
import { BuildingCard } from "components/card/buildingCard";
import { BuildingDTO } from "shared/types/building";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Switch, Tooltip, Pagination, Spinner } from "@heroui/react";
import { List, Windows, Stack } from "assets/icons";
import { toast } from "react-toastify";
import ButtonNew from "components/buttonNew/buttonNew";
import { Trash, Eye, CarProfile, BuildingApartment, TrashIcon, Certificate, Heart } from "@phosphor-icons/react";
import { dueDiligenceCache } from "shared/services/dueDiligenceCacheService";
import { UniversalTable } from "shared/components/UniversalTable";
import { useLazyLoading } from "shared/components/UniversalTable/hooks/useLazyLoading";
import { BUILDINGS_TABLE_CONFIG } from "./buildingsTableConfig";
import { Link } from "react-router-dom";
import { applyMask } from "shared/utils/masks";
import { DueDiligenceDotsInline } from "./components/DueDiligenceDotsInline";
import { HelpdeskTicketsInline } from "./components/HelpdeskTicketsInline";
import { Checkbox } from "@/components/ui/checkbox";

const Buildings = () => {
  const { session } = useSession();

  // View mode state - default to list view
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return (localStorage.getItem('buildings-view-mode') as 'grid' | 'list') || 'list';
  });
  const isGridView = viewMode === 'grid';

  // Search and filter state
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [buildingTypeFilter, setBuildingTypeFilter] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string>("none");

  // Filter options loaded from backend (all companies + building types)
  const [filterOptionsData, setFilterOptionsData] = useState<{
    companies: { id: number; name: string }[];
    buildingTypes: string[];
  }>({ companies: [], buildingTypes: [] });

  // Grid view state and pagination
  const [buildings, setBuildings] = useState<BuildingDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(8); // Number of buildings per page for grid view
  const parentRef = useRef<HTMLDivElement>(null);

  // Debounce search text to avoid hitting the backend on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Build additionalParams for server-side filtering
  const additionalParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (companyFilter.length > 0) params.companyIds = companyFilter.join(',');
    if (buildingTypeFilter.length > 0) params.buildingTypes = buildingTypeFilter.join(',');
    return params;
  }, [debouncedSearch, companyFilter, buildingTypeFilter]);

  // List view lazy loading - now with server-side filtering via additionalParams
  const lazyLoading = useLazyLoading({
    endpoint: `${API_URL}/building/summaries`,
    pageSize: 15,
    sortField: 'id',
    sortDirection: 'DESC',
    additionalParams,
    transformResponse: (result: any) => {
      return result.map((building: any) => ({
        ...building,
        ticketCount: building.ticketCount ?? 0,
        grossActualRentalIncomePerYear: building.grossActualRentalIncomePerYear ?? 0
      }));
    },
    onFetchError: () => {
      toast.error("Er is iets misgegaan bij het ophalen van de gebouwen");
    }
  });

  // Fetch filter options from dedicated endpoint (all companies + building types)
  useEffect(() => {
    if (!session) return;

    const fetchFilterOptions = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/building/filter-options`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const data = await response.json();
          setFilterOptionsData(data);
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchFilterOptions();
  }, [session]);

  // Fetch buildings for grid view with server-side filtering and pagination
  const fetchBuildingsForGrid = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: limit.toString(),
        sortField: 'id',
        sortDirection: 'DESC',
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (companyFilter.length > 0) params.set('companyIds', companyFilter.join(','));
      if (buildingTypeFilter.length > 0) params.set('buildingTypes', buildingTypeFilter.join(','));

      const response = await fetchWithAuth(
        `${API_URL}/building/summaries?${params.toString()}`,
        { method: "GET", signal },
        session,
      );

      if (!response.ok) {
        toast.error("Er is iets misgegaan bij het ophalen van de gebouwen");
        throw new Error("Failed to fetch buildings");
      }

      const result = await response.json();

      // Process buildings to ensure expected properties exist
      const buildingData = (result.data || []).map((building: any) => ({
        ...building,
        ticketCount: building.ticketCount ?? 0,
        grossActualRentalIncomePerYear: building.grossActualRentalIncomePerYear ?? 0
      }));

      const total = result.totalCount || buildingData.length;
      const calculatedTotalPages = Math.ceil(total / limit);

      setBuildings(buildingData);
      setTotalPages(calculatedTotalPages);
      setIsLoading(false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching buildings:", error);
      toast.error("Er is iets misgegaan bij het ophalen van de gebouwen");
      setIsLoading(false);
    }
  }, [session, page, limit, debouncedSearch, companyFilter, buildingTypeFilter]);

  // Filter sections using options from dedicated backend endpoint
  const filterSections = useMemo(() => [
    {
      key: 'company',
      label: 'Bedrijf',
      options: filterOptionsData.companies.map(c => ({
        key: String(c.id),
        label: c.name
      })),
      color: 'brick' as const,
      multiSelect: true,
    },
    {
      key: 'buildingType',
      label: 'Gebouwtype',
      options: filterOptionsData.buildingTypes.map(type => ({
        key: type,
        label: type
      })),
      color: 'moss' as const,
      multiSelect: true,
    }
  ], [filterOptionsData]);

  // Grouping options with extractors for complex fields
  const groupByOptions = useMemo(() => [
    { key: 'none', label: 'Geen' },
    {
      key: 'company',
      label: 'Per bedrijf',
      extractor: (building: any) => building.company?.name || 'Geen bedrijf'
    },
    {
      key: 'buildingType',
      label: 'Per gebouwtype',
      extractor: (building: any) => building.buildingType || 'Geen type'
    }
  ], []);

  // Filter change handlers
  const handleFilterChange = useCallback((key: string, values: string[]) => {
    if (key === 'company') {
      setCompanyFilter(values);
    } else if (key === 'buildingType') {
      setBuildingTypeFilter(values);
    }
  }, []);

  // Initial data fetch for grid view - when page or filters change
  useEffect(() => {
    if (!session || viewMode !== 'grid') return;

    const controller = new AbortController();
    fetchBuildingsForGrid(controller.signal);

    return () => {
      controller.abort();
    };
  }, [viewMode, page, session, fetchBuildingsForGrid]);

  // Reset grid page to 1 when filters change
  useEffect(() => {
    if (viewMode === 'grid') {
      setPage(1);
    }
  }, [debouncedSearch, companyFilter, buildingTypeFilter, viewMode]);

  const clearBuildingCache = () => {
    // Clear all cache formats
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('buildings_') || key === 'buildings') {
        localStorage.removeItem(key);
      }
    });

    // Clear the new due diligence cache service
    dueDiligenceCache.clearAll();

    // Log cache statistics before clearing
    const stats = dueDiligenceCache.getStats();
    console.log('[Buildings] Clearing cache, stats:', stats);

    toast.success(`Cache gewist (${stats.total} due diligence berekeningen verwijderd)`);

    // Reload data based on view mode
    if (viewMode === 'list') {
      lazyLoading.fetch(false); // Reset and fetch from page 1
    } else {
      setPage(1); // Grid view will refetch via useEffect
    }
  };

  const handleViewChange = (isGrid: boolean) => {
    const newMode = isGrid ? 'grid' : 'list';
    setViewMode(newMode);
    localStorage.setItem('buildings-view-mode', newMode);

    // Reset data for new view
    if (newMode === 'list') {
      // lazyLoading hook will automatically fetch on mount
      // No need to manually trigger
    } else {
      setPage(1); // Grid view will fetch via useEffect
    }
  };

  // Render building row for table view
  const renderBuildingRow = useCallback((building: any) => {
    return [
      // Checkbox
      {
        id: 'checkbox',
        content: (
          <Checkbox />
        )
      },
      // ID
      {
        id: 'id',
        content: (
          <Link
            to={`/building/${building.id}`}
            className="text-neutral-primary hover:text-moss-primary font-medium"
          >
            {building.id}
          </Link>
        )
      },
      // Address
      {
        id: 'address',
        content: (
          <Link
            to={`/building/${building.id}`}
            className="text-neutral-primary hover:text-moss-primary flex items-center gap-2"
          >
            <BuildingApartment weight="duotone" size={16} />
            <span>{building.googlePlaceLongAddress || '-'}</span>
          </Link>
        )
      },
      // Company
      {
        id: 'company',
        content: building.company?.name ? (
          <span className="text-neutral-primary">{building.company.name}</span>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Units
      {
        id: 'units',
        content: (
          <div className="flex items-center gap-1">
            <Stack height={14} />
            <span className="text-neutral-primary">
              {typeof building.units === 'number' ? building.units : building.units?.length || 0}
            </span>
          </div>
        )
      },
      // Tickets
      {
        id: 'tickets',
        content: <HelpdeskTicketsInline building={building} />
      },
      // Due Diligence
      {
        id: 'dueDiligence',
        content: <DueDiligenceDotsInline building={building} />
      },
      // Cash Out
      {
        id: 'cashOut',
        content: building.totalCashOutIncludingRenovation ? (
          <span className="text-neutral-primary">
            {applyMask(building.totalCashOutIncludingRenovation, "valuta")}
          </span>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Rent
      {
        id: 'rent',
        content: building.grossActualRentalIncomePerYear ? (
          <span className="text-moss-primary font-medium">
            {applyMask(building.grossActualRentalIncomePerYear, "valuta")}
          </span>
        ) : (
          <div className="flex items-center gap-2">
            <Spinner color="warning" size="sm" />
            <span className="text-xs text-neutral-secondary-mid">Berekenen...</span>
          </div>
        )
      },
      // Mobiscore
      {
        id: 'mobiscore',
        content: building.mobiscore ? (
          <div className="flex items-center gap-1">
            <CarProfile weight="duotone" size={14} />
            <span className="text-neutral-primary font-medium">{building.mobiscore}</span>
          </div>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // BREEAM
      {
        id: 'breeam',
        content: building.breeamRating ? (
          <div className="flex items-center gap-1">
            <Certificate weight="duotone" size={14} />
            <span className="text-moss-primary font-medium">{building.breeamRating}</span>
          </div>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // WELL
      {
        id: 'well',
        content: building.wellRating ? (
          <div className="flex items-center gap-1">
            <Heart weight="duotone" size={14} />
            <span className="text-brick-primary font-medium">{building.wellRating}</span>
          </div>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Actions
      {
        id: 'actions',
        content: (
          <Link to={`/building/${building.id}`}>
            <ButtonNew
              variant="secondaryNeutral"
              icon={<Eye weight="duotone" size={18} />}
              label="Bekijken"
            />
          </Link>
        )
      }
    ];
  }, []);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  // Show loading skeleton only for grid view initial load
  if (viewMode === 'grid' && isLoading) {
    return (
      <div className="md:shadow z-10 grid w-full grid-cols-1 gap-4 rounded-sm md:grid-cols-2 md:p-4 xl:grid-cols-3 2xl:grid-cols-4">
        {[...Array(limit)].map((_, idx) => (
          <BuildingSkeleton key={idx} />
        ))}
      </div>
    );
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Gebouwen &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>
      <section className="mb-10 flex flex-col gap-4">
        <div className="pt-4 flex items-center justify-end flex-wrap gap-2 text-sm font-medium">
          <div className="flex items-center gap-2">
            <List
              className={`h-[18px] w-[18px] ${
                !isGridView ? "text-brick-primary" : "text-neutral-secondary-mid"
              }`}
            />
            <Switch
              defaultSelected={isGridView}
              size="sm"
              onChange={() => handleViewChange(!isGridView)}
              classNames={{
                wrapper:
                  "mr-0 group-data-[selected=true]:bg-neutral-primary/20 bg-neutral-primary/20 shadow-[inset_0_0_0_1px_rgba(49,64,59,0.08)]",
              }}
            />
            <Windows
              className={`h-[19px] w-[19px] ${
                isGridView ? "text-brick-primary" : "text-neutral-secondary-mid"
              }`}
            />
            <Tooltip
              content="Gezien er veel data achter onze gebouwen zit duurt dit eventjes om op te halen uit de database en de berekeningen uit te voeren. Daarom wordt de meest 'statische' data eenmalig gecached de eerste keer zodat dit scherm nadien wat sneller kan laden. Door de cache te clearen haal je echt alle meest recente data nogmaals op.

            Het volgende is altijd live data:
            1) Helpdesktickets
            2) Actuele bruto huur/jaar
            "
              containerPadding={50}
              classNames={{
                base: "max-w-[700px]",
                content:
                  "max-h-[200px] top-0 overflow-y-scroll items-start justify-start p-3 font-normal leading-normal",
              }}
            >
              <div>
                <ButtonNew
                  variant="secondaryNeutral"
                  icon={<Trash weight="duotone" size={18} />}
                  onClick={clearBuildingCache}
                  label="Clear cache"
                />
              </div>
            </Tooltip>
          </div>
        </div>

        {/* List View - UniversalTable with lazy loading */}
        {viewMode === 'list' ? (
          <>
            <UniversalTable
              {...BUILDINGS_TABLE_CONFIG}
              tableId="buildings"
              rows={lazyLoading.data}
              title="Gebouwen"
              description="Beheer al uw gebouwen"
              renderRow={renderBuildingRow}
              groupBy={groupBy}
              searchConfig={{
                searchPlaceholder: "Zoek op ID, adres of bedrijf...",
                filterSections: filterSections,
                groupByOptions: groupByOptions,
                onGroupByChange: setGroupBy,
                searchValue: searchText,
                onSearchChange: setSearchText,
                activeFilters: {
                  company: companyFilter,
                  buildingType: buildingTypeFilter
                },
                onFilterChange: handleFilterChange,
                showSpacer: false
              }}
              excelExportConfig={{
                filename: 'gebouwen',
                sheetName: 'Gebouwen',
                buttonText: 'Naar Excel exporteren',
                excludeColumns: ['checkbox', 'actions', 'dueDiligence'],
                columnMapping: {
                  'id': 'id',
                  'address': 'googlePlaceLongAddress',
                  'company': 'company',
                  'units': 'units',
                  'tickets': 'ticketCount',
                  'cashOut': 'totalCashOutIncludingRenovation',
                  'rent': 'grossActualRentalIncomePerYear',
                  'mobiscore': 'mobiscore',
                  'breeam': 'breeamRating',
                  'well': 'wellRating'
                }
              }}
            />
            {lazyLoading.isLoadingMore && (
              <div className="flex justify-center py-4">
                <Spinner size="lg" />
              </div>
            )}
            {lazyLoading.hasMore && !lazyLoading.isLoadingMore && (
              <div className="flex justify-center py-4">
                <ButtonNew
                  variant="secondaryNeutral"
                  label="Laad meer gebouwen"
                  onClick={() => lazyLoading.fetch(true)}
                />
              </div>
            )}
            {!lazyLoading.hasMore && lazyLoading.data.length > 0 && (
              <div className="text-center py-4 text-neutral-secondary-mid text-sm">
                Alle gebouwen geladen ({lazyLoading.data.length} totaal)
              </div>
            )}
            {/* Infinite scroll trigger */}
            <div ref={lazyLoading.observerTarget} className="h-10" />
          </>
        ) : (
          <>
            {/* Grid View - BuildingCard components with pagination */}
            <div
              ref={parentRef}
              className="z-10 grid w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
            >
              {buildings && buildings.length > 0 ? (
                buildings.map((building) => {
                  const buildingData = { ...building };

                  return (
                    <div key={building.id || Math.random()}>
                      <BuildingCard building={buildingData} />
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full py-10 text-center text-neutral-primary">
                  <p className="mb-2 text-lg font-medium">Geen gebouwen gevonden</p>
                  <p className="text-sm">Probeer de cache te clearen en opnieuw te laden.</p>
                  <div className="mt-4">
                    <ButtonNew
                      icon={<TrashIcon weight="duotone" size={18} />}
                      onClick={clearBuildingCache}
                      label="Clear cache"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Pagination Controls for Grid View */}
            {totalPages > 1 && (
              <div className="mt-4 flex justify-center">
                <Pagination
                  total={totalPages}
                  initialPage={page}
                  page={page}
                  onChange={handlePageChange}
                  showControls
                  color="primary"
                  classNames={{
                    cursor: "bg-primary-foreground text-primary",
                    item: "text-foreground bg-muted",
                    prev: "text-foreground bg-muted",
                    next: "text-foreground bg-muted",
                  }}
                />
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default Buildings;
